/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DialProfileGeneralSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Dial Profiles module.
----------------------------------------------------------------*/


import { PhoneCall } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import Toggle from '../../../components/ui/Toggle';

export type AllowByValue = 'none' | 'caller' | 'recipient' | 'both';
export type CallScreeningValue = 'disabled' | 'ask_once' | 'always_ask';

export interface DialProfileFormData {
    name: string;
    allowTransferBy: AllowByValue;
    allowParkBy: AllowByValue;
    ringbackTone: string;
    callScreening: CallScreeningValue;
    customOptions: string;
    ringingTone: boolean;
    description: string;
    active: boolean;
}

interface DialProfileGeneralSectionProps {
    formData: DialProfileFormData;
    errors: Record<string, string>;
    isLoading: boolean;
    selectedRingbackPreset: string;
    customRingbackValue: string;
    onFieldChange: <K extends keyof DialProfileFormData>(field: K, value: DialProfileFormData[K]) => void;
    onRingbackPresetChange: (value: string) => void;
    t: (key: string) => string;
    disabledName?: boolean;
}

export function DialProfileGeneralSection({
    formData, errors, isLoading, selectedRingbackPreset, customRingbackValue,
    onFieldChange, onRingbackPresetChange, t, disabledName,
}: DialProfileGeneralSectionProps) {
    return (
        <FormBox
            title={t('dialProfiles.sections.general')}
            description={t('dialProfiles.sections.generalDescription')}
            icon={<PhoneCall className="h-5 w-5" />}
            isLoading={isLoading}
        >
            <div className="grid grid-cols-8 items-start gap-x-6 gap-y-4">

                {/* Name */}
                <SimpleTooltip content={t('dialProfiles.fields.name.tooltip')} className="col-span-2">
                    <Label htmlFor="name" className="text-left">
                        {t('dialProfiles.fields.name.label')}
                        <span className="ml-1 text-destructive">*</span>
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => onFieldChange('name', e.target.value)}
                        placeholder={t('dialProfiles.fields.name.placeholder')}
                        error={errors.name}
                        disabled={disabledName}
                    />
                </div>

                {/* Description */}
                <SimpleTooltip content={t('dialProfiles.fields.description.tooltip')} className="col-span-2">
                    <Label htmlFor="description" className="text-left">
                        {t('dialProfiles.fields.description.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Input
                        id="description"
                        value={formData.description}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        placeholder={t('dialProfiles.fields.description.placeholder')}
                    />
                </div>

                {/* Allow Transfer By */}
                <SimpleTooltip content={t('dialProfiles.fields.allowTransferBy.tooltip')} className="col-span-2">
                    <Label htmlFor="allowTransferBy" className="text-left">
                        {t('dialProfiles.fields.allowTransferBy.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <select
                        id="allowTransferBy"
                        value={formData.allowTransferBy}
                        onChange={(e) => onFieldChange('allowTransferBy', e.target.value as AllowByValue)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="none">{t('dialProfiles.fields.allowTransferBy.options.none')}</option>
                        <option value="caller">{t('dialProfiles.fields.allowTransferBy.options.caller')}</option>
                        <option value="recipient">{t('dialProfiles.fields.allowTransferBy.options.recipient')}</option>
                        <option value="both">{t('dialProfiles.fields.allowTransferBy.options.both')}</option>
                    </select>
                </div>

                {/* Allow Park By */}
                <SimpleTooltip content={t('dialProfiles.fields.allowParkBy.tooltip')} className="col-span-2">
                    <Label htmlFor="allowParkBy" className="text-left">
                        {t('dialProfiles.fields.allowParkBy.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <select
                        id="allowParkBy"
                        value={formData.allowParkBy}
                        onChange={(e) => onFieldChange('allowParkBy', e.target.value as AllowByValue)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="none">{t('dialProfiles.fields.allowParkBy.options.none')}</option>
                        <option value="caller">{t('dialProfiles.fields.allowParkBy.options.caller')}</option>
                        <option value="recipient">{t('dialProfiles.fields.allowParkBy.options.recipient')}</option>
                        <option value="both">{t('dialProfiles.fields.allowParkBy.options.both')}</option>
                    </select>
                </div>

                {/* Ringback Tone */}
                <SimpleTooltip content={t('dialProfiles.fields.ringbackTone.tooltip')} className="col-span-2">
                    <Label htmlFor="ringbackTonePreset" className="text-left">
                        {t('dialProfiles.fields.ringbackTone.label')}
                        <span className="ml-1 text-destructive">*</span>
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2 space-y-2">
                    <select
                        id="ringbackTonePreset"
                        value={selectedRingbackPreset}
                        onChange={(e) => onRingbackPresetChange(e.target.value)}
                        className={`w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary ${errors.ringbackTone ? 'border-destructive focus:ring-destructive' : ''}`}
                    >
                        <option value="default">{t('dialProfiles.fields.ringbackTone.options.default')}</option>
                        <option value="none">{t('dialProfiles.fields.ringbackTone.options.none')}</option>
                        <option value="test">{t('dialProfiles.fields.ringbackTone.options.test')}</option>
                        <option value="music/default">{t('dialProfiles.fields.ringbackTone.options.musicDefault')}</option>
                        <option value="custom">{t('dialProfiles.fields.ringbackTone.options.custom')}</option>
                    </select>
                    {selectedRingbackPreset === 'custom' ? (
                        <Input
                            id="ringbackToneCustom"
                            value={customRingbackValue}
                            onChange={(e) => onFieldChange('ringbackTone', e.target.value)}
                            placeholder={t('dialProfiles.fields.ringbackTone.customPlaceholder')}
                            error={errors.ringbackTone}
                        />
                    ) : errors.ringbackTone ? (
                        <p className="text-xs text-destructive">{errors.ringbackTone}</p>
                    ) : null}
                </div>

                {/* Call Screening */}
                <SimpleTooltip content={t('dialProfiles.fields.callScreening.tooltip')} className="col-span-2">
                    <Label htmlFor="callScreening" className="text-left">
                        {t('dialProfiles.fields.callScreening.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <select
                        id="callScreening"
                        value={formData.callScreening}
                        onChange={(e) => onFieldChange('callScreening', e.target.value as CallScreeningValue)}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        <option value="disabled">{t('dialProfiles.fields.callScreening.options.disabled')}</option>
                        <option value="ask_once">{t('dialProfiles.fields.callScreening.options.askOnce')}</option>
                        <option value="always_ask">{t('dialProfiles.fields.callScreening.options.alwaysAsk')}</option>
                    </select>
                </div>

                {/* Ringing Tone */}
                <SimpleTooltip content={t('dialProfiles.fields.ringingTone.tooltip')} className="col-span-2">
                    <Label htmlFor="ringingTone" className="text-left">
                        {t('dialProfiles.fields.ringingTone.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Toggle id="ringingTone" value={formData.ringingTone} onChange={(v) => onFieldChange('ringingTone', v)} />
                </div>

                {/* Active */}
                <SimpleTooltip content={t('dialProfiles.fields.active.tooltip')} className="col-span-2">
                    <Label htmlFor="active" className="text-left">
                        {t('dialProfiles.fields.active.label')}
                    </Label>
                </SimpleTooltip>
                <div className="col-span-2">
                    <Toggle id="active" value={formData.active} onChange={(v) => onFieldChange('active', v)} />
                </div>

            </div>
        </FormBox>
    );
}
